import { Fragment, PropsWithChildren, ReactElement, ReactNode, startTransition, useRef, useState } from "react"
import { Drawer, DrawerProps } from "./Drawer"
import { Popover, PopoverProps } from "./Popover"
import { useHydrating } from "eddev/routing"
import { useBreakpoint } from "@hooks/useBreakpoint"
import { Slot } from "@radix-ui/react-slot"

type Props = {
  popover: Omit<PopoverProps, "trigger" | "anchor" | "open" | "onOpenChange" | "children">
  drawer: Omit<DrawerProps, "trigger" | "open" | "onOpenChange" | "children">
  trigger: ReactElement
  children: ReactNode | ((props: { close: () => void; as: "drawer" | "popover" }) => ReactNode)
}

/**
 * Renders a popover on desktop, and a drawer on mobile.
 */
export function ResponsivePopover(props: Props) {
  const hydrating = useHydrating()

  const [open, setOpen] = useState(false)

  const breakpoint = useBreakpoint()
  const isMobile = breakpoint === "BASE" || breakpoint === "sm"

  if (hydrating) return props.trigger

  const children =
    typeof props.children === "function"
      ? props.children({ close: () => setOpen(false), as: isMobile ? "drawer" : "popover" })
      : props.children

  return (
    <Fragment>
      {isMobile ? (
        <Drawer
          {...props.drawer}
          trigger={props.trigger}
          open={open}
          onOpenChange={(open) => startTransition(() => setOpen(open))}
        >
          {children}
        </Drawer>
      ) : (
        <Popover
          {...props.popover}
          trigger={props.trigger}
          open={open}
          onOpenChange={(open) => startTransition(() => setOpen(open))}
        >
          {children}
        </Popover>
      )}
    </Fragment>
  )
}
